window.NPLP = window.NPLP || {};
window.NPLP.instances = window.NPLP.instances || {};
window.NPLP.LPProductFormHandler = class LPProductFormHandler {

/**
 * Initializes the LPProductFormHandler.
 *
 * Required CSS classes and locations:
 * - .lpfp-pack: Applied to each product pack/variant container
 * - .lpfp-totals: Container for total price information
 * - .lpfp-total-price: Element displaying the total price
 * - .lpfp-total-percent-off: Element displaying the total percentage off
 * - .lpfp-total-compare-at-price: Element displaying the total compare-at price
 * - .lpfp-total-saved-price: Element displaying the total saved price
 * - .lpfp-total-subscription-price: Element displaying the subscription price
 * - .lpfp-submit: Submit button for the form
 * - .lpfp-price-per-pack: Element displaying the price per pack (inside .lpfp-pack)
 * - .lpfp-price: Element displaying the pack price (inside .lpfp-pack)
 * - .lpfp-compare-at-price: Element displaying the pack compare-at price (inside .lpfp-pack)
 * - .lpfp-saved: Element displaying the pack saved amount (inside .lpfp-pack)
 * - .lpfp-percentage-off: Element displaying the pack percentage off (inside .lpfp-pack)
 *
 * Optional CSS classes:
 * - .lpfp--dma-subscription-override: Added to #lpfp-product-form when DMA subscription override is enabled
 * - .lpfp--has-multiple-packs: Added to .lpfp-totals and .lpfp-pack when subscriptions are available
 * - .lpfp--has-subscription: Added to .lpfp-totals when subscriptions are available
 * - .lpfp--has-subscription-frequency: Added to #lpfp-product-form when subscription frequency is enabled
 * - .lpfp--is-discounted: Added to .lpfp-totals and .lpfp-pack when there's a discount
 * - .lpfp--no-discount--hide: Elements with this class are hidden when there's no discount
 * - .hidden: Used to hide elements
 *
 * Example HTML structure:
 * <div id="lp-product-form">
 *   <ul class="lpfp-pack-list">
 *     <li class="lpfp-pack"
 *         data-id="SKU123"
 *         data-name="Pack 1"
 *         data-price="19.99"
 *         data-compare-at-price="24.99"
 *         data-selling-plan-id="789"
 *         data-subscription-percent-off="10"
 *         data-pack-count="3">
 *       <span class="lpfp-price-per-pack"></span>
 *       <span class="lpfp-price"></span>
 *       <span class="lpfp-compare-at-price"></span>
 *       <span class="lpfp-saved"></span>
 *       <span class="lpfp-percentage-off"></span>
 *     </li>
 *     <!-- More .lpfp-pack elements as list items -->
 *   </ul>
 *   <input type="checkbox" class="lpfp-enable-subscriptions" />
 *   <select class="lpfp-subscription-frequencies"></select>
 *   <div class="lpfp-totals">
 *     <span class="lpfp-total-price"></span>
 *     <span class="lpfp-total-percent-off"></span>
 *     <span class="lpfp-total-compare-at-price"></span>
 *     <span class="lpfp-total-saved-price"></span>
 *     <span class="lpfp-total-subscription-price"></span>
 *   </div>
 *   <button class="lpfp-submit">Add to Cart</button>
 * </div>
 *
 * @param {Object} options - Configuration options for the form handler.
 * @param {string} [options.instanceName="LPProductFormHandler"] - Name of this instance.
 * @param {boolean} [options.debug=false] - Enable debug logging.
 * @param {string|jQuery} [options.container="#lp-product-form"] - Selector or jQuery object for the form container.
 * @param {string} [options.activeVariantClass="lpfp-selected-variant"] - Class name for the active variant.
 */
  constructor(options = {}) {
    this.instanceName = options.instanceName || "LPProductFormHandler";
    this.debug = options.debug || false;
    this.logLevel = options.logLevel || "info";
    this.containerQuery = options.container || "#lp-product-form";
    this.container = $(this.containerQuery);
    this.log = this.createLogger();
    this.packsEls = this.container.find(".lpfp-pack");
    this.initialVariantAttrs = options.initialVariantAttrs || false;

    this.totalsEl = this.container.find(".lpfp-totals");
    this.totalPriceEl = this.container.find(".lpfp-total-price");
    this.totalPercentOffEl = this.container.find(".lpfp-total-percent-off");
    this.totalCompareAtPriceEl = this.container.find(
      ".lpfp-total-compare-at-price"
    );
    this.totalSavedPriceEl = this.container.find(".lpfp-total-saved-price");

    this.subscriptionPriceEl = this.container.find(
      ".lpfp-total-subscription-price"
    );

    this.submitEl = this.container.find(".lpfp-submit");
    this.activeVariantClass =
      options.activeVariantClass || "lpfp-selected-variant";
    this.perPackFormat = options.perPackFormat || "%s Each";

    this.dmaSubscriptionOverride = false;
    this.useSubscriptions = false;
    this.subscriptionFrequency = false;
    this.restrictFrequencies = options.restrictFrequencies !== undefined ? options.restrictFrequencies : true;
    this.permittedSubscriptionFrequencies = ["Every 2 Months", "Every 1 Month", "Every 3 Months"];
    this.enableSubscriptionsEl = this.container.find('.lpfp-enable-subscriptions');
    this.subscriptionFrequencySelectEl = this.container.find('.lpfp-subscription-frequencies');

    // Variant group toggler (e.g., dog/cat)
    this.activeVariantGroup = options.variantGroup || null;
    this.variantGroupBtns = this.container.find('.lpfp-variant-group-btn');

    // Plan type toggle (card-based UI alternative to checkbox)
    this.planTypeToggle = options.planTypeToggle || false;
    this.planTypeEls = this.container.find('.lpfp-plan-type');
    this.activePlanType = options.initialPlanType || 'one-time';

    // Add-ons
    this.addonEls = this.container.find('.lpfp-addon');

    // Sub-option selector (color / flavor)
    this.subOptionEls = this.container.find('.lpfp-suboption__swatch');
    this.subVariantsData = {};
    this.activeSubOption = null;

    if (window.LPProductForm?.isInitialized) {
      this.initFormBindings();
    } else {
      const checkInterval = setInterval(() => {
        if (window.LPProductForm?.isInitialized) {
          clearInterval(checkInterval);
          this.initFormBindings();
        }
      }, 100);
    }
  }

  createLogger() {
    const logLevels = {
      debug: 0,
      info: 1,
      success: 2,
      error: 3
      };

    const colors = {
      debug: "#9b59b6",
      info: "#3498db",
      success: "#2ecc71",
      error: "#e74c3c",
      };

    const currentLogLevel = logLevels[this.logLevel] || 0;

    const logFn = (type, ...args) => {
      if (this.debug && logLevels[type] >= currentLogLevel) {
        console.log(
          `%cNP-${this.instanceName}`,
          `background: ${colors[type]}; color: white; padding: 2px 5px; border-radius: 3px;`,
          ...args
        );
      }
      };

    return {
      debug: (...args) => logFn("debug", ...args),
      info: (...args) => logFn("info", ...args),
      success: (...args) => logFn("success", ...args),
      error: (...args) => logFn("error", ...args),
    };
  }

  parsePrice(priceString) {
    let normalized = priceString;

    // Handle comma-as-decimal format (e.g., "36,00 €" or "1.234,56 €")
    // Pattern: digits, comma, exactly 2 digits = comma-decimal locale
    if (/\d+,\d{2}(?!\d)/.test(normalized)) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }

    const price = parseFloat(normalized.replace(/[^\d.-]/g, "")) * 100;
    return price;
  }

  updateTotalEls() {
    this.log.debug("updateTotalEls()");
    const totals = window.LPProductForm.getTotal();
    const formatted = totals.formatted;

    this.log.debug(`Totals: ${totals.total.packs} packs, ${this.subscriptionFrequency} subscription frequency`);
    this.container.toggleClass('lpfp--has-multiple-packs', totals.total.packs > 1 && this.subscriptionFrequency !== false);

    this.totalPriceEl.text(formatted.price);
    this.totalPercentOffEl.text(formatted.percentageOff);
    this.totalCompareAtPriceEl.text(formatted.compareAtPrice);
    this.totalSavedPriceEl.text(formatted.saved);

    if (formatted.hasSubscriptions === true) {
      this.log.debug("hasSubscriptions");
      this.totalsEl.addClass("lpfp--has-subscription");
      this.subscriptionPriceEl.text(formatted.subscriptionPrice);
    } else {
      this.log.debug("hasSubscriptions is false");
      this.totalsEl.removeClass("lpfp--has-subscription");
    }

    const isDiscounted = totals.total.saved > 0; 
    this.log.debug(`isDiscounted: ${isDiscounted}`);
    this.totalsEl.toggleClass('lpfp--is-discounted', isDiscounted);
    this.totalsEl.find('.lpfp--no-discount--hide').toggleClass('hidden', !isDiscounted);
  }

  restrictToSubscriptionOnly() {
    this.log.info("restrictToSubscriptionOnly()");
    this.container.addClass('lpfp--dma-subscription-override');
    this.dmaSubscriptionOverride = true;
    this.update();
  }

  getOrCreatePackInfo(pack) {
    this.log.debug('getOrCreatePackInfo()', pack, pack.attr('data-qty'));
    let info = pack.data('packInfo');
    if (!info) {
      info = {
        sku: pack.attr("data-id"),
        name: pack.attr("data-name"),
        price: this.parsePrice(pack.attr("data-price")),
        compareAtPrice: this.parsePrice(pack.attr("data-compare-at-price") || pack.attr('data-price')),
        sellingPlanId: pack.attr("data-selling-plan-id"),
        sellingPlanPercentOff: pack.attr("data-subscription-percent-off") || 0,
        availableSellingPlans: pack.find('.lpfp-subscription-frequency option').map(function() {
          return {
            id: $(this).attr('value'),
            name: $(this).data('name'),
            percentOff: parseFloat($(this).data('percent-off') || 0),
          }
        }).get(),
        packCount: pack.attr("data-pack-count") || 1,
      };
    } else {
      // Keep cached pack info in sync with live DOM attrs (color/flavor/group changes)
      info.sku = pack.attr("data-id");
      info.name = pack.attr("data-name");
      info.price = this.parsePrice(pack.attr("data-price"));
      info.compareAtPrice = this.parsePrice(pack.attr("data-compare-at-price") || pack.attr('data-price'));
      info.packCount = pack.attr("data-pack-count") || info.packCount || 1;
    }
    // always update qty
    info.quantity = pack.attr("data-qty") ? parseInt(pack.attr("data-qty")) : 1;

    pack.data('packInfo', info);

    this.log.debug('getOrCreatePackInfo() returning', info);
    return info;
  }

  updateFormEls() {
    this.log.debug("updateFormEls()");
    this.packsEls.each((_, element) => {
      const pack = $(element);
      const info = this.getOrCreatePackInfo(pack);
      const { raw, formatted } = window.LPProductForm.getVariantTotals(info);
      this.log.debug(`Updating variant ${info.name}`, info, raw, formatted);
      
      if (formatted.pricePerPack) {
        pack.find(".lpfp-price-per-pack")?.text(this.perPackFormat.replace("%s", formatted.pricePerPack));
      }
      var isV2Bundle = pack.hasClass('lpfp-bundle') && pack.closest('.lpfp-bundle__wrap--v2').length > 0;
      var displayPrice = isV2Bundle && formatted.pricePerPack ? formatted.pricePerPack : formatted.price;
      pack.find(".lpfp-price")?.text(displayPrice);
      pack.find(".lpfp-compare-at-price")?.text(formatted.compareAtPrice);
      pack.find(".lpfp-saved")?.text(formatted.saved);
      pack.find(".lpfp-percentage-off")?.text(formatted.percentageOff);

      const isDiscounted = raw.saved > 0;
      pack.toggleClass('lpfp--is-discounted', isDiscounted);
      pack.find('.lpfp--no-discount--hide').toggleClass('hidden', !isDiscounted);
    });
  }

  updateSubscriptionFrequencies(availableSellingPlans) {
    this.log.debug("updateSubscriptionFrequencies()");
    availableSellingPlans = [...new Set(availableSellingPlans.map(JSON.stringify))].map(JSON.parse);

    // Exclude non-subscription plans (Try Now / TBYB / etc.) that can appear when
    // a product has multiple selling plan groups.
    availableSellingPlans = availableSellingPlans.filter((plan) => {
      const name = String(plan.name || '');
      if (!name) return false;
      const lower = name.toLowerCase();
      if (lower.includes('try before') || lower.includes('trynow') || lower.includes('tbyb')) {
        return false;
      }
      return /^every\s+/i.test(name);
    });

    // Deduplicate by normalized frequency name (same label, different plan IDs)
    const seenNames = new Set();
    availableSellingPlans = availableSellingPlans.filter((plan) => {
      const key = String(plan.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!key || seenNames.has(key)) return false;
      seenNames.add(key);
      return true;
    });

    if (this.restrictFrequencies) {
      const orderedLowerPermittedFrequencies = this.permittedSubscriptionFrequencies.map(frequency => frequency.toLowerCase());
      availableSellingPlans = availableSellingPlans.filter(plan => orderedLowerPermittedFrequencies.includes(plan.name.toLowerCase()));
      availableSellingPlans.sort((a, b) => {
        const order = orderedLowerPermittedFrequencies;
        return order.indexOf(a.name.toLowerCase()) - order.indexOf(b.name.toLowerCase());
      });
    }
    // Clear existing options
    this.subscriptionFrequencySelectEl.empty();

    if (availableSellingPlans.length === 0) {
      this.subscriptionFrequency = false;
      this.log.debug("No available selling plans found");
      this.container.removeClass('lpfp--has-multiple-packs');
      return;
    }

    const findPlan = (freq) => {
      if (!freq) return null;
      const freqStr = String(freq);
      const freqLower = freqStr.toLowerCase();
      return availableSellingPlans.find((plan) =>
        String(plan.id) === freqStr ||
        plan.name === freqStr ||
        plan.name.toLowerCase() === freqLower ||
        plan.name.toLowerCase() === `every ${freqLower}`
      ) || null;
    };

    // Prefer current selection, then "Every 2 Months", then first plan
    let matched = findPlan(this.subscriptionFrequency) ||
      findPlan(this.subscriptionFrequencySelectEl.val());
    if (!matched) {
      matched = availableSellingPlans.find((plan) =>
        /every\s*2\s*months?/i.test(plan.name) || /2\s*months?/i.test(plan.name)
      ) || availableSellingPlans[0];
    }
    this.subscriptionFrequency = matched.name;
    this.log.debug(`Using subscription frequency: ${this.subscriptionFrequency}`);

    // Add new options from availableSellingPlans
    availableSellingPlans.forEach(plan => {
      const option = $('<option>', {
        value: plan.name,
        text: plan.name,
        'data-plan-id': plan.id,
        'data-name': plan.name,
      });
      if (plan.name === this.subscriptionFrequency) {
        this.log.debug(`Setting subscription frequency to ${plan.name}`);
        option.prop('selected', true);
      }
      this.subscriptionFrequencySelectEl.append(option);
    });

    // Keep the visible "Delivers every:" label in sync with the real selection
    const displayText = String(this.subscriptionFrequency).replace(/^every\s+/i, '');
    this.container.find('.lpfp-subscr__freq-display').text(displayText);

    // Re-sync LPProductForm if subscriptions are currently active. Without this,
    // switching active variants while on the subscription plan can leave
    // LPProductForm.subscriptionFrequency stale (false) even though the
    // dropdown has valid options.
    if (this.useSubscriptions) {
      window.LPProductForm.setSubscriptionFrequency(this.subscriptionFrequency);
    }

    this.log.debug("Subscription frequencies updated");
  }

  updateLPForm(container) {
    if (container === undefined) {
      container = this.container.first();
    }

    this.log.debug(`Updating LP form for container: ${container.attr('id')}`);
    const selectedPacks = container.find(
      `.lpfp-pack.${this.activeVariantClass}`
    );
    const skus = [];
    let availableSellingPlans = [];
    let sellingPlansChanged = false;

    if (selectedPacks.length > 0) {
      selectedPacks.each((_, element) => {
        const info = this.getOrCreatePackInfo($(element));
        this.log.debug(`updateLPForm() - Option selected: ${info.name}`, info);
        skus.push(info);

        // Update available selling plans
        if (info.availableSellingPlans) {
          availableSellingPlans.push(...info.availableSellingPlans);
        }
      });

      if (this.dmaSubscriptionOverride === false) {
        // Sort and stringify the new selling plans for comparison
        const newSellingPlansStr = JSON.stringify(availableSellingPlans.sort((a, b) => a.id - b.id));

        // Compare with the previous selling plans
        if (!this.previousSellingPlansStr || this.previousSellingPlansStr !== newSellingPlansStr) {
          sellingPlansChanged = true;
          this.previousSellingPlansStr = newSellingPlansStr;
        }

        // Only update the DOM if selling plans have changed
        if (sellingPlansChanged) {
          this.updateSubscriptionFrequencies(availableSellingPlans);
        }
      }

      window.LPProductForm.setSKUs(skus);
    }

    // Append addon SKUs after main SKUs
    const addonSKUs = this.getAddonSKUs();
    addonSKUs.forEach((addonSKU) => {
      window.LPProductForm.addSKU(addonSKU);
    });
  }

  updatePlanEls() {
    if (!this.planTypeToggle || this.planTypeEls.length === 0) return;

    // Only update if plan cards have price elements
    var subPriceEl = this.planTypeEls.filter('[data-plan="subscription"]').find('.lpfp-plan__price-new');
    if (subPriceEl.length === 0) return;

    // Both plan cards reflect whichever pack (or bundle) is active.
    var activePack = this.container.find('.lpfp-pack.' + this.activeVariantClass).first();
    if (activePack.length === 0) return;

    var info = this.getOrCreatePackInfo(activePack);
    var oneTimePrice = info.price;
    var hasSellingPlans = !!(info.availableSellingPlans && info.availableSellingPlans.length > 0);

    // Disable Subscribe when the active variant has no selling plan allocations
    // (e.g. SunnyPatch 1 Pack). Prevents 422 "Cannot apply selling plan to variant".
    var subPlanEl = this.planTypeEls.filter('[data-plan="subscription"]');
    subPlanEl.toggleClass('lpfp--is-disabled', !hasSellingPlans);
    subPlanEl.attr('aria-disabled', hasSellingPlans ? 'false' : 'true');
    this.container.toggleClass('lpfp--subscription-unavailable', !hasSellingPlans);

    // Subscription %: prefer the active pack's own selling plan; if absent
    // (e.g. a bundle product with no subscription), fall back to the first
    // pack that does have one so the Subscribe card still shows informational
    // savings.
    var percentOff = 0;
    if (hasSellingPlans) {
      percentOff = info.availableSellingPlans[0].percentOff || 0;
    }
    if (percentOff === 0) {
      var self = this;
      this.packsEls.each(function () {
        var fallback = self.getOrCreatePackInfo($(this));
        if (fallback.availableSellingPlans && fallback.availableSellingPlans.length > 0) {
          var pct = fallback.availableSellingPlans[0].percentOff || 0;
          if (pct > 0) {
            percentOff = pct;
            return false; // break
          }
        }
      });
    }

    var subPrice = percentOff > 0 ? oneTimePrice * (1 - percentOff / 100) : oneTimePrice;

    // Update plan card prices
    subPriceEl.text(window.LPProductForm.formatMoney(subPrice));
    this.planTypeEls.filter('[data-plan="subscription"]').find('.lpfp-plan__price-old').text(window.LPProductForm.formatMoney(oneTimePrice));
    this.planTypeEls.filter('[data-plan="one-time"]').find('.lpfp-plan__price-new').text(window.LPProductForm.formatMoney(oneTimePrice));
  }

  update(container) {
    this.log.info("Updating…");
    // Force one-time before totals when active variant has no selling plans
    if (this.planTypeToggle && this.activePlanType === 'subscription') {
      const activePack = this.container.find('.lpfp-pack.' + this.activeVariantClass).first();
      if (activePack.length) {
        const info = this.getOrCreatePackInfo(activePack);
        if (!info.availableSellingPlans || info.availableSellingPlans.length === 0) {
          this.setPlanType('one-time', true);
        }
      }
    }
    this.updateAddonStates();
    this.updateLPForm(container);
    this.updateTotalEls();
    this.updateFormEls();
    this.updatePlanEls();
  }

  packClicked(pack) {
    this.log.info("packClicked()");
    $(pack)
      .addClass(this.activeVariantClass)
      .siblings()
      .removeClass(this.activeVariantClass);
    this.update(pack.closest(this.containerQuery));
  }

  triggerSubscriptionsEl(show) {
    this.log.info(`triggerSubscriptionsEl(${show})`);

    this.useSubscriptions = show;
    this.container.toggleClass('lpfp--has-subscription-frequency', show);

    if (show === true) {
      // Prefer the selected option's plan name (handles both Liquid value=id
      // and JS-rebuilt value=name). Fall back to handler.subscriptionFrequency.
      var $opt = this.subscriptionFrequencySelectEl.find('option:selected');
      var freq = $opt.data('name') || $opt.attr('data-name') || $opt.val() ||
        this.subscriptionFrequencySelectEl.val() || this.subscriptionFrequency;
      // If we somehow still have a numeric selling plan id, resolve to name
      if (freq && /^\d+$/.test(String(freq))) {
        var byId = this.subscriptionFrequencySelectEl.find('option[data-plan-id="' + freq + '"], option[value="' + freq + '"]').first();
        freq = byId.data('name') || byId.attr('data-name') || byId.text() || freq;
      }
      this.subscriptionFrequency = freq;
      window.LPProductForm.setSubscriptionFrequency(freq);
      this.container.find('.subscribe-frequency').slideDown();
    } else {
      window.LPProductForm.setSubscriptionFrequency(false);
      this.container.find('.subscribe-frequency').slideUp();
    }
  }

  resetFormEls() {
    this.log.info("resetFormEls()");
    this.packsEls.removeClass(this.activeVariantClass);

    if (this.dmaSubscriptionOverride === true) this.restrictToSubscriptionOnly();
    this.triggerSubscriptionsEl(false);

    if (this.initialVariantAttrs !== false) {
      this.packsEls.filter(this.initialVariantAttrs).addClass(this.activeVariantClass);
    }
    this.update();
  }

  // ── Variant Group Toggler ────────────────────────────────────
  initVariantGroupToggler() {
    if (!this.activeVariantGroup || this.variantGroupBtns.length === 0) return;

    this.log.info("initVariantGroupToggler()", this.activeVariantGroup);

    // Set initial active state
    this.variantGroupBtns.removeClass('lpfp--is-active active');
    this.variantGroupBtns.filter('[data-variant-group="' + this.activeVariantGroup + '"]').addClass('lpfp--is-active active');

    // Apply initial group to data-id
    this.setVariantGroup(this.activeVariantGroup, true);

    this.variantGroupBtns.on('click', (e) => {
      const btn = $(e.currentTarget);
      const groupName = btn.attr('data-variant-group');
      if (groupName === this.activeVariantGroup) return;

      this.variantGroupBtns.removeClass('lpfp--is-active active');
      btn.addClass('lpfp--is-active active');
      this.setVariantGroup(groupName);
    });
  }

  setVariantGroup(groupName, skipUpdate) {
    this.log.info("setVariantGroup()", groupName);
    this.activeVariantGroup = groupName;
    const attrName = 'data-' + groupName + '-id';

    this.packsEls.each((_, element) => {
      const pack = $(element);
      const groupId = pack.attr(attrName);
      if (groupId) {
        pack.attr('data-id', groupId);
        pack.removeData('packInfo');
      }
    });

    // Toggle pack/bundle image visibility so the active group's image is shown.
    // Works for any slug (dog/cat, kid/adult, etc.) by iterating known toggler buttons.
    const self = this;
    this.variantGroupBtns.each(function () {
      const g = $(this).attr('data-variant-group');
      const isActive = g === groupName;
      self.container
        .find('.lpfp-pack__image-' + g + ', .lpfp-bundle__image-' + g)
        .toggleClass('active', isActive);
    });

    if (!skipUpdate) {
      this.update();
    }

    this.container[0].dispatchEvent(new CustomEvent('lpfp:variantGroupChanged', {
      detail: { group: groupName },
      bubbles: true,
    }));
  }

  // ── Sub-option selector (Color / Flavor) ───────────────────
  initSubOptionSelector() {
    if (!this.subOptionEls || this.subOptionEls.length === 0) return;

    this.log.info("initSubOptionSelector()");

    const dataEl = this.container.find('.lpfp-subvariants-data')[0];
    if (dataEl) {
      try {
        this.subVariantsData = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.log.error("Failed to parse subvariants data", e);
        this.subVariantsData = {};
      }
    }

    const activeEl = this.subOptionEls.filter('.active').first();
    this.activeSubOption = (activeEl.length ? activeEl : this.subOptionEls.first()).attr('data-value');
    this.applySubOption(this.activeSubOption, true);

    this.subOptionEls.on('click', (e) => {
      e.preventDefault();
      const btn = $(e.currentTarget);
      const value = btn.attr('data-value');
      if (!value || value === this.activeSubOption) return;

      this.subOptionEls.removeClass('active').attr('aria-selected', 'false');
      btn.addClass('active').attr('aria-selected', 'true');
      this.applySubOption(value);
    });
  }

  applySubOption(value, skipUpdate) {
    this.log.info("applySubOption()", value);
    this.activeSubOption = value;
    const data = this.subVariantsData || {};

    this.packsEls.not('.lpfp-bundle').each((_, element) => {
      const pack = $(element);
      const pk = String(pack.attr('data-pack-count') || '1');
      const packMap = data[pk] || data[Number(pk)] || null;
      if (!packMap) {
        this.log.warning("applySubOption: no pack map for", pk, data);
        return;
      }
      const entry = packMap[value];
      if (!entry) {
        this.log.warning("applySubOption: no entry for", value, "in pack", pk, packMap);
        return;
      }

      pack.attr('data-id', String(entry.id));
      if (entry.price) pack.attr('data-price', entry.price);
      if (entry.compareAtPrice) pack.attr('data-compare-at-price', entry.compareAtPrice);
      if (entry.name) pack.attr('data-name', entry.name);

      // Rebuild per-variant selling plan options (some packs e.g. 1 Pack may have none)
      const $freq = pack.find('.lpfp-subscription-frequency');
      if ($freq.length) {
        $freq.empty();
        (entry.sellingPlans || []).forEach((plan) => {
          const $opt = $('<option></option>')
            .attr('value', plan.id)
            .attr('data-name', plan.name)
            .attr('data-percent-off', plan.percentOff || 0)
            .text(plan.name);
          $freq.append($opt);
        });
      }

      // Clear both jQuery cache keys that can retain the old variant
      pack.removeData('packInfo');
      if (pack[0]) $.removeData(pack[0], 'packInfo');

      if (entry.image) {
        const img = pack.find('.lpfp-pack__image img.active').first();
        if (img.length) {
          img.attr('src', entry.image);
        } else {
          pack.find('.lpfp-pack__image img').first().attr('src', entry.image);
        }
      }

      // Flavor-only: update visible pack title to the selected variant name
      if (this.container.find('.lpfp-packages--flavor-only').length) {
        const titleBits = (entry.name || '').split(' - ');
        const label = titleBits.length > 1 ? titleBits.slice(1).join(' - ') : (entry.name || value);
        pack.find('.lpfp-pack__title').first().text(label);
      }
    });

    if (!skipUpdate) {
      this.update();
    }

    this.container[0].dispatchEvent(new CustomEvent('lpfp:subOptionChanged', {
      detail: { value },
      bubbles: true,
    }));
  }

  // ── Plan Type Toggle ───────────────────────────────────────
  initPlanTypeToggle() {
    if (!this.planTypeToggle || this.planTypeEls.length === 0) return;

    this.log.info("initPlanTypeToggle()");
    this.container.addClass('lpfp--has-plan-type-toggle');

    // Set initial state
    this.setPlanType(this.activePlanType, true);

    this.planTypeEls.on('click', (e) => {
      const el = $(e.currentTarget);
      if (el.hasClass('lpfp--is-disabled') || el.attr('aria-disabled') === 'true') return;
      const planType = el.attr('data-plan');
      if (planType === this.activePlanType) return;
      this.setPlanType(planType);
    });
  }

  setPlanType(planType, skipUpdate) {
    this.log.info("setPlanType()", planType);
    this.activePlanType = planType;

    this.planTypeEls.removeClass('lpfp--is-active active');
    this.planTypeEls.filter('[data-plan="' + planType + '"]').addClass('lpfp--is-active active');

    this.container.toggleClass('lpfp--plan-subscription', planType === 'subscription');
    this.container.toggleClass('lpfp--plan-one-time', planType === 'one-time');

    const isSubscription = planType === 'subscription';
    this.triggerSubscriptionsEl(isSubscription);

    if (!skipUpdate) {
      this.update();
    }
  }

  // ── Add-on Module ──────────────────────────────────────────
  initAddons() {
    if (this.addonEls.length === 0) return;

    this.log.info("initAddons()", this.addonEls.length, "addons found");

    // Counter add-ons
    this.container.on('click', '.lpfp-addon__counter-plus', (e) => {
      const addon = $(e.currentTarget).closest('.lpfp-addon');
      if (addon.hasClass('lpfp--addon-disabled')) return;
      const valueEl = addon.find('.lpfp-addon__counter-value');
      var value = parseInt(valueEl.text()) || 0;
      value++;
      valueEl.text(value);
      this.updateAddonStates();
      this.update();
    });

    this.container.on('click', '.lpfp-addon__counter-minus', (e) => {
      const addon = $(e.currentTarget).closest('.lpfp-addon');
      if (addon.hasClass('lpfp--addon-disabled')) return;
      const valueEl = addon.find('.lpfp-addon__counter-value');
      var value = parseInt(valueEl.text()) || 0;
      if (value > 0) {
        value--;
        valueEl.text(value);
      }
      this.updateAddonStates();
      this.update();
    });

    // Counter subscribe toggle
    this.container.on('click', '.lpfp-addon__subscribe-btn', (e) => {
      const btn = $(e.currentTarget);
      const addon = btn.closest('.lpfp-addon');
      if (addon.hasClass('lpfp--addon-disabled')) return;
      addon.find('.lpfp-addon__subscribe-btn').removeClass('lpfp--is-active active');
      btn.addClass('lpfp--is-active active');
      this.update();
    });

    // Pack-selector add-ons
    this.container.on('click', '.lpfp-addon__pack-option', (e) => {
      const option = $(e.currentTarget);
      const addon = option.closest('.lpfp-addon');
      if (addon.hasClass('lpfp--addon-disabled')) return;

      if (option.hasClass('lpfp--is-active')) {
        option.removeClass('lpfp--is-active');
        addon.removeClass('lpfp--addon-active');
      } else {
        addon.find('.lpfp-addon__pack-option').removeClass('lpfp--is-active');
        option.addClass('lpfp--is-active');
        addon.addClass('lpfp--addon-active');
      }
      this.update();
    });

    // Pack-selector toggle (expand/collapse)
    this.container.on('click', '.lpfp-addon__pack-toggle', (e) => {
      const addon = $(e.currentTarget).closest('.lpfp-addon');
      if (addon.hasClass('lpfp--addon-disabled')) return;
      addon.find('.lpfp-addon__pack-options').slideToggle();
      addon.toggleClass('lpfp--addon-expanded');
    });

    this.updateAddonStates();
  }

  getAddonSellingPlans(addon) {
    const raw = addon.attr('data-addon-selling-plans');
    if (raw) {
      try {
        const plans = JSON.parse(raw);
        if (Array.isArray(plans) && plans.length) {
          return plans.map((plan) => ({
            id: String(plan.id),
            name: plan.name,
            percentOff: parseFloat(plan.percentOff) || 0,
          }));
        }
      } catch (e) {
        this.log.error('Failed to parse addon selling plans', e);
      }
    }

    // Fallback: single default plan from data attrs
    const id = addon.attr('data-addon-selling-plan-id');
    const percentOff = parseFloat(addon.attr('data-addon-subscription-percent-off') || 0);
    if (id && id !== '0') {
      return [{ id: String(id), name: '', percentOff }];
    }
    return [];
  }

  matchAddonSellingPlan(plans, frequency) {
    if (!plans || !plans.length || !frequency) return null;
    const freqStr = String(frequency);
    const lcFrequency = freqStr.toLowerCase();
    return plans.find((plan) => {
      const planName = String(plan.name || '').toLowerCase();
      return String(plan.id) === freqStr ||
        planName === lcFrequency ||
        planName === `every ${lcFrequency}` ||
        (lcFrequency.length >= 5 && planName.includes(lcFrequency));
    }) || null;
  }

  getAddonSKUs() {
    const skus = [];

    this.addonEls.each((_, element) => {
      const addon = $(element);
      if (addon.hasClass('lpfp--addon-disabled')) return;

      if (addon.hasClass('lpfp-addon--counter')) {
        const qty = parseInt(addon.find('.lpfp-addon__counter-value').text()) || 0;
        if (qty > 0) {
          const subscribeBtn = addon.find('.lpfp-addon__subscribe-btn.lpfp--is-active');
          const wantsSubscription = subscribeBtn.attr('data-addon-subscribe') === 'true';
          const isSubscriptionActive = this.activePlanType === 'subscription';

          const sku = {
            sku: addon.attr('data-addon-id'),
            quantity: qty,
            name: addon.attr('data-addon-name'),
            price: parseInt(addon.attr('data-addon-price')) || 0,
            compareAtPrice: parseInt(addon.attr('data-addon-compare-at-price') || addon.attr('data-addon-price')) || 0,
            packCount: 1,
            availableSellingPlans: [],
          };

          if (wantsSubscription && isSubscriptionActive) {
            const plans = this.getAddonSellingPlans(addon);
            sku.availableSellingPlans = plans;

            const freq = this.subscriptionFrequency ||
              (window.LPProductForm && window.LPProductForm.getSubscriptionFrequency()) ||
              false;
            const matched = this.matchAddonSellingPlan(plans, freq) || plans[0] || null;

            if (matched) {
              sku.sellingPlanId = matched.id;
              sku.sellingPlanPercentOff = matched.percentOff;
              this.log.debug('Addon selling plan matched', {
                addon: sku.name,
                frequency: freq,
                sellingPlanId: matched.id,
                planName: matched.name,
              });
            }
          }

          skus.push(sku);
        }
      } else if (addon.hasClass('lpfp-addon--pack-selector')) {
        const activeOption = addon.find('.lpfp-addon__pack-option.lpfp--is-active');
        if (activeOption.length > 0) {
          skus.push({
            sku: activeOption.attr('data-addon-id'),
            quantity: 1,
            name: addon.attr('data-addon-name') + ' - ' + (activeOption.attr('data-addon-pack-count') || '1') + ' Pack',
            price: parseInt(activeOption.attr('data-addon-price')) || 0,
            compareAtPrice: parseInt(activeOption.attr('data-addon-compare-at-price') || activeOption.attr('data-addon-price')) || 0,
            packCount: parseInt(activeOption.attr('data-addon-pack-count')) || 1,
            availableSellingPlans: [],
          });
        }
      }
    });

    this.log.debug("getAddonSKUs()", skus);
    return skus;
  }

  updateAddonStates() {
    const isSubscription = this.activePlanType === 'subscription';

    this.addonEls.each((_, element) => {
      const addon = $(element);

      if (addon.hasClass('lpfp-addon--counter')) {
        const qty = parseInt(addon.find('.lpfp-addon__counter-value').text()) || 0;
        addon.toggleClass('lpfp--addon-active', qty > 0);

        // Show subscribe toggle only when qty > 0 and subscription plan is active
        const showSubscribeToggle = qty > 0 && isSubscription;
        const toggleEl = addon.find('.lpfp-addon__subscribe-toggle');
        if (showSubscribeToggle) {
          toggleEl.slideDown();
        } else {
          toggleEl.slideUp();
        }

        // Reflect the subscription discount in the addon's displayed price only
        // when the user is actually on subscription plan AND has chosen "Add to
        // Subscription" (the subscribe button). "Purchase Separately" reverts
        // the price to one-time. The `.active` class highlights the discounted
        // price in red.
        const subscribeBtn = addon.find('.lpfp-addon__subscribe-btn.lpfp--is-active');
        const wantsSubscription = subscribeBtn.attr('data-addon-subscribe') === 'true';
        const actuallyDiscounted = isSubscription && wantsSubscription;

        const priceEl = addon.find('.lpfp-addon__price');
        if (priceEl.length > 0) {
          const basePrice = parseInt(addon.attr('data-addon-price')) || 0;
          const percentOff = parseFloat(addon.attr('data-addon-subscription-percent-off')) || 0;
          const isDiscounted = actuallyDiscounted && percentOff > 0;
          const displayPrice = isDiscounted ? basePrice * (1 - percentOff / 100) : basePrice;
          priceEl.text(window.LPProductForm.formatMoney(displayPrice));
          priceEl.toggleClass('active', isDiscounted);
        }
      }
    });
  }

  initFormBindings() {
    this.log.info("initFormBindings()");
    this.initVariantGroupToggler();
    this.initSubOptionSelector();
    this.resetFormEls();

    this.packsEls.on("click", (e) => {
      if (e.target && e.target.classList.contains('btn-tooltip')) {
        return false;
      }
      
      this.log.error('packClicked() started', e, e.currentTarget);
      this.packClicked(e.currentTarget);
      return false;
    });

    this.enableSubscriptionsEl.on("click", (e) => {
      const showSubscriptionsEl = this.container.hasClass('lpfp--has-subscription-frequency') ? false : true;
      this.triggerSubscriptionsEl(showSubscriptionsEl);
      this.update($(e.target).closest(this.containerQuery));

      return false;
    });

    this.subscriptionFrequencySelectEl.on("change", (e) => {
      const $opt = $(e.target).find("option:selected");
      const selectedFrequency = $opt.data("name") || $opt.attr("data-name") || $(e.target).val();

      this.subscriptionFrequency = selectedFrequency;
      this.log.info(`Setting subscription frequency to ${this.subscriptionFrequency}`);

      // Update the "Delivers every:" display text
      const displayText = String(selectedFrequency).replace(/^every\s+/i, '');
      this.container.find('.lpfp-subscr__freq-display').text(displayText);

      // Ensure we're using the most recent value
      window.LPProductForm.setSubscriptionFrequency(this.subscriptionFrequency);

      // Update the form to reflect the new frequency
      this.update($(e.target).closest(this.containerQuery));
    });

    this.submitEl.click(async (event) => {
      event.preventDefault();
      this.log.info("Form submission intercepted");
      await window.LPProductForm.submitCart();
    });

    window.addEventListener("locGateSubsAdded", () => {
      this.restrictToSubscriptionOnly();
    });

    if (window.locGated === true) {
      this.restrictToSubscriptionOnly();
    }

    this.initPlanTypeToggle();
    this.initAddons();

    // Final update to ensure all features are synced after init
    this.update();

    // Check if v2 bundle card is tall (title wraps) and adjust price position
    this.container.find('.lpfp-bundle__wrap--v2 .lpfp-bundle').each(function() {
      var el = $(this);
      if (el[0].offsetHeight > 80) el.addClass('lpfp-bundle--tall');
    });

    this.log.success("initFormBindings() - Form initialization complete");
  }
};


// ══════════════════════════════════════════════════════════════════════════
// Form State Machine
//
// A modular state machine for the lpv2 product form. Features plug in via
// `reduce` (pure state transition), `invariants` (post-reduce fix-up),
// `render` (DOM side effects), and `bind` (event wiring).
//
// Features included here: PackPlanFeature (base), BundleFeature.
// Future features (addons, variant groups) can be added as new modules
// without touching the core or other features.
// ══════════════════════════════════════════════════════════════════════════

/**
 * Resolve the DOM element for a variant key.
 *   'bundle'  → the .lpfp-bundle element
 *   '3' / '4' → .lpfp-pack[data-pack-count="N"] (excluding bundle)
 */
function _findVariantEl(variantKey, handler) {
  if (variantKey === 'bundle') {
    return handler.container.find('.lpfp-bundle').first();
  }
  return handler.container
    .find('.lpfp-pack[data-pack-count="' + variantKey + '"]')
    .not('.lpfp-bundle')
    .first();
}

/**
 * PackPlanFeature — the base form module.
 * Owns: active variant (pack or bundle), plan type (one-time/subscription).
 * Handles: pack clicks, bundle clicks, plan clicks.
 */
window.NPLP.PackPlanFeature = {
  initialState(handler) {
    const active = handler.container.find('.lpfp-pack.' + handler.activeVariantClass).first();
    let variant = null;
    if (active.length) {
      variant = active.hasClass('lpfp-bundle') ? 'bundle' : active.attr('data-pack-count');
    }
    return {
      variant,
      plan: handler.activePlanType || 'one-time',
    };
  },

  reduce(state, action) {
    switch (action.type) {
      case 'PACK_CLICK':
        return { ...state, variant: action.variant };
      case 'BUNDLE_CLICK':
        return { ...state, variant: 'bundle' };
      case 'PLAN_CLICK':
        return { ...state, plan: action.plan };
      default:
        return state;
    }
  },

  invariants(state, handler) {
    // Chosen variant must exist in the DOM; otherwise fall back to first non-bundle pack.
    if (!_findVariantEl(state.variant, handler).length) {
      const fallback = handler.container.find('.lpfp-pack').not('.lpfp-bundle').first();
      if (fallback.length) {
        return { ...state, variant: fallback.attr('data-pack-count') };
      }
    }

    // Variants without selling plan allocations can't be subscribed
    // (Shopify returns 422 "Cannot apply selling plan to variant").
    if (state.plan === 'subscription') {
      const el = _findVariantEl(state.variant, handler);
      if (el.length) {
        const info = handler.getOrCreatePackInfo(el);
        if (!info.availableSellingPlans || info.availableSellingPlans.length === 0) {
          return { ...state, plan: 'one-time' };
        }
      }
    }
    return state;
  },

  render(state, handler) {
    const cls = handler.activeVariantClass;
    // Active variant class: clear all, then set on the target.
    handler.container.find('.lpfp-pack.' + cls).removeClass(cls);
    const target = _findVariantEl(state.variant, handler);
    if (target.length) target.addClass(cls);

    // Plan type: delegate to handler.setPlanType (which handles classes + update()).
    if (handler.activePlanType !== state.plan) {
      handler.setPlanType(state.plan);
    } else {
      handler.update();
    }
  },

  bind(sm, handler) {
    // Replace the default packClicked so pack + bundle clicks dispatch through the SM.
    handler.packClicked = function (packEl) {
      const $p = $(packEl);
      if ($p.hasClass('lpfp-bundle')) {
        sm.dispatch({ type: 'BUNDLE_CLICK' });
      } else {
        sm.dispatch({ type: 'PACK_CLICK', variant: $p.attr('data-pack-count') });
      }
    };
    // Replace plan-type clicks: un-bind the default, rebind through the SM.
    handler.planTypeEls.off('click');
    handler.planTypeEls.on('click', function () {
      const $el = $(this);
      if ($el.hasClass('lpfp--is-disabled') || $el.attr('aria-disabled') === 'true') return;
      sm.dispatch({ type: 'PLAN_CLICK', plan: $el.attr('data-plan') });
    });
  },
};

/**
 * BundleFeature — adds bundle-specific invariants + the "swap on subscribe" behavior.
 *   - Selecting the bundle forces plan to one-time.
 *   - Switching to subscription while bundle is active swaps the active variant
 *     to the configured default pack (e.g. '3').
 */
window.NPLP.BundleFeature = {
  options: { defaultPackVariant: '3' },

  _defaultPack(handler) {
    var preferred = window.NPLP.BundleFeature.options.defaultPackVariant;
    if (_findVariantEl(preferred, handler).length) return preferred;
    var fallback = handler.container.find('.lpfp-pack').not('.lpfp-bundle').first();
    return fallback.length ? fallback.attr('data-pack-count') : preferred;
  },

  _isV2(handler) {
    if (!handler || !handler.container) return false;
    return handler.container.find('.lpfp-bundle__wrap--v2').length > 0;
  },

  _bundleHasPlans(handler) {
    if (!handler || !handler.container) return false;
    var wrap = handler.container.find('.lpfp-bundle__wrap--v2');
    return wrap.length > 0 && wrap.attr('data-has-selling-plans') === 'true';
  },

  reduce(state, action, handler) {
    var v2 = window.NPLP.BundleFeature._isV2(handler);
    var hasPlans = v2 && window.NPLP.BundleFeature._bundleHasPlans(handler);

    if (v2 && hasPlans) return state;

    if (action.type === 'BUNDLE_CLICK') {
      return { ...state, plan: 'one-time' };
    }
    if (
      action.type === 'PLAN_CLICK' &&
      state.variant === 'bundle' &&
      action.plan === 'subscription'
    ) {
      if (v2) return state;
      return { ...state, variant: window.NPLP.BundleFeature._defaultPack(handler) };
    }
    return state;
  },

  invariants(state, handler) {
    var v2 = window.NPLP.BundleFeature._isV2(handler);
    if (v2 && window.NPLP.BundleFeature._bundleHasPlans(handler)) return state;
    if (state.variant === 'bundle' && state.plan === 'subscription') {
      if (v2) return { ...state, plan: 'one-time' };
      return { ...state, variant: window.NPLP.BundleFeature._defaultPack(handler) };
    }
    return state;
  },
};

/**
 * FormStateMachine — core store + dispatcher. Features do the work.
 */
window.NPLP.FormStateMachine = class FormStateMachine {
  constructor(handler, features = []) {
    this.handler = handler;
    this.features = features;
    this.state = {};
  }

  dispatch(action) {
    let next = this.state;
    for (const f of this.features) {
      if (f.reduce) next = f.reduce(next, action, this.handler);
    }
    this.state = this._runInvariants(next);
    this._render();
  }

  getState() {
    return { ...this.state };
  }

  init() {
    // Merge initial state from every feature.
    for (const f of this.features) {
      if (f.initialState) {
        this.state = { ...this.state, ...f.initialState(this.handler) };
      }
    }
    this.state = this._runInvariants(this.state);
    // Wire up event bindings.
    for (const f of this.features) {
      if (f.bind) f.bind(this, this.handler);
    }
    this._render();
  }

  _runInvariants(state) {
    let next = state;
    // Fixed-point iteration so feature invariants can cascade.
    let changed;
    let iterations = 0;
    do {
      changed = false;
      for (const f of this.features) {
        if (!f.invariants) continue;
        const after = f.invariants(next, this.handler);
        if (after !== next) {
          next = after;
          changed = true;
        }
      }
      iterations += 1;
    } while (changed && iterations < 10);
    return next;
  }

  _render() {
    for (const f of this.features) {
      if (f.render) f.render(this.state, this.handler);
    }
  }
};

